import {
  CuiLookup,
  type CuiLookupCompany,
  Input,
  Select,
  Switch,
  Text,
  toast,
} from '@pcrm/fe/ui-kit';
import { countyName, RO_COUNTY_LIST } from '@pcrm/shared/address';
import { type CompanyIconKey, companyIconKeys, DEFAULT_COMPANY_ICON } from '@pcrm/shared/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Field } from '../../components/field';
import { ApiError } from '../../lib/api';
import { SESSION_QUERY_KEY } from '../auth/session';
import { lookupCompanyByCui } from './anaf.api';
import {
  COMPANIES_QUERY_KEY,
  type Company,
  type CompanyInput,
  createCompany,
  updateCompany,
} from './companies.api';
import { companyIcon, companyIconLabel } from './company-icons';

/**
 * Datele firmei — identitate, date fiscale, bancă, adresă — ca formular.
 *
 * Două locuri îl folosesc (#207): sertarul de adăugare din listă și tabul „Setări firmă" din
 * ecranul firmei. Starea stă într-un hook, câmpurile într-o componentă, pentru că butoanele
 * diferă: sertarul le are în subsol, ecranul în antetul cardului de date.
 *
 * ⚠️ **Formularul ține doar câmpurile firmei.** Logo-ul, semnătura și seria de facturi se
 * salvează pe loc, pe rutele lor, iar „Salvează" de aici nu le poartă.
 */

/** Rândul, în forma în care îl editează formularul: `null` devine șir gol. */
export function toInput(company: Company): CompanyInput {
  return {
    name: company.name,
    shortName: company.shortName ?? '',
    cif: company.cif,
    icon: company.icon,
    tradeRegisterNo: company.tradeRegisterNo ?? '',
    vatPayer: company.vatPayer,
    iban: company.iban ?? '',
    ibanEur: company.ibanEur ?? '',
    treasuryIban: company.treasuryIban ?? '',
    bankName: company.bankName ?? '',
    shareCapital: company.shareCapital ?? '',
    invoiceContactName: company.invoiceContactName ?? '',
    invoiceContactPhone: company.invoiceContactPhone ?? '',
    invoiceContactEmail: company.invoiceContactEmail ?? '',
    phone: company.phone ?? '',
    email: company.email ?? '',
    billingAddress: company.billingAddress ?? '',
    legalAddress: company.legalAddress ?? '',
    city: company.city ?? '',
    countyCode: company.countyCode ?? '',
    postalCode: company.postalCode ?? '',
    status: company.status,
  };
}

/**
 * Erorile de la server → câmpul căruia îi aparțin.
 *
 * Două surse, pentru că serverul are două feluri de refuz:
 *
 * - **`validation_error`** duce `issues: [{ path, message }]`, unde `path` e chiar numele
 *   câmpului din contract (`ZodValidationPipe`);
 * - **erorile de domeniu** n-au `issues`, dar au un `code` stabil care spune despre ce e
 *   vorba. `company_cif_taken` e despre CIF, deci ajunge sub CIF — nu într-un toast pe
 *   care operatorul îl citește după ce s-a uitat deja la formular.
 */
const ERROR_CODE_FIELDS: Record<string, keyof CompanyInput> = {
  company_cif_taken: 'cif',
};

function toFieldErrors(error: unknown): Partial<Record<keyof CompanyInput, string>> {
  if (!(error instanceof ApiError)) {
    return {};
  }

  const byCode = ERROR_CODE_FIELDS[error.code];
  if (byCode) {
    return { [byCode]: error.message };
  }

  const fields: Partial<Record<keyof CompanyInput, string>> = {};
  for (const issue of error.issues) {
    // `path` vine din contract, deci e deja numele câmpului. Ce nu se potrivește cu niciun
    // câmp din formular se ignoră aici și cade pe toast — mai bine văzut undeva decât nicăieri.
    if (issue.path in EMPTY) {
      fields[issue.path as keyof CompanyInput] = issue.message;
    }
  }

  return fields;
}

/**
 * Câmpurile pe care le poate aduce ANAF.
 *
 * Denumirea, Reg. Com. și adresa, **câmp cu câmp** (#183): ANAF trimite strada, localitatea,
 * județul și codul poștal separat, iar lipirea lor într-un singur rând era exact ce făcea
 * adresa imposibil de pus în XML-ul e-Factura. Restul (IBAN, bancă, icon, denumire scurtă) nu
 * există în registrul public, iar `status` e al nostru, nu al lor.
 */
const ANAF_FIELDS = [
  'name',
  'tradeRegisterNo',
  'legalAddress',
  'city',
  'countyCode',
  'postalCode',
] as const;
type AnafField = (typeof ANAF_FIELDS)[number];

const EMPTY: CompanyInput = {
  name: '',
  shortName: '',
  cif: '',
  icon: DEFAULT_COMPANY_ICON,
  tradeRegisterNo: '',
  // Firmă nouă = plătitoare, ca implicitul coloanei. Excepția se comută la adăugare.
  vatPayer: true,
  iban: '',
  ibanEur: '',
  treasuryIban: '',
  bankName: '',
  shareCapital: '',
  invoiceContactName: '',
  invoiceContactPhone: '',
  invoiceContactEmail: '',
  phone: '',
  email: '',
  billingAddress: '',
  legalAddress: '',
  city: '',
  countyCode: '',
  postalCode: '',
  status: true,
};

/** `Select` nu acceptă valoare goală; asta e „necompletat". */
const NO_COUNTY = '_fara';

export type CompanyFormState = ReturnType<typeof useCompanyForm>;

/**
 * Starea formularului și salvarea lui.
 *
 * Pornește **o singură dată** de la firmă, la montare — nu la fiecare citire a listei. Pe
 * ecranul firmei, lista se recitește la revenirea în fereastră; un formular care s-ar
 * reseta după ea ar șterge, tăcut, ce tastase operatorul înainte să schimbe fereastra. Altă
 * firmă înseamnă altă montare (`key` pe id, la apelant).
 */
export function useCompanyForm({
  company,
  onSaved,
}: {
  company: Company | null;
  onSaved?: (saved: Company) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CompanyInput>(() => (company ? toInput(company) : EMPTY));
  const [errors, setErrors] = useState<Partial<Record<keyof CompanyInput, string>>>({});
  /** Ce a întors ANAF pentru câmpurile pe care operatorul le scrisese deja. Vezi `applyAnaf`. */
  const [fromAnaf, setFromAnaf] = useState<Partial<Record<keyof CompanyInput, string>>>({});

  /**
   * Formularul, citit din afara randării.
   *
   * `applyAnaf` e chemată din promisiunea căutării, adică oricând până la timeout-ul de 5s
   * al serverului — timp în care operatorul poate să fi tastat în IBAN. Închiderea lexicală
   * ar aduce cu ea formularul de la momentul apăsării și i-ar șterge, tăcut, ce a scris
   * între timp.
   */
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const set = (field: keyof CompanyInput, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    // Eroarea dispare la prima tastă din câmpul ei, nu abia la următoarea salvare: altfel
    // operatorul corectează IBAN-ul și rămâne cu roșu sub el, fără să știe dacă a nimerit.
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    // La fel și propunerea de la ANAF: dacă a trecut prin câmp și a scris el, a ales.
    setFromAnaf((current) => (current[field] ? { ...current, [field]: undefined } : current));
  };

  /**
   * Rezultatul căutării → formular.
   *
   * **Câmpurile goale se completează, cele scrise nu se suprascriu.** Regula are un motiv
   * concret: operatorul care a tastat denumirea de mână și abia apoi caută nu are cum să
   * afle că i-a fost înlocuită — o suprascriere tăcută se descoperă la citirea
   * contractului. Ce diferă rămâne vizibil sub câmp, cu un buton de înlocuit; alegerea e
   * a lui.
   *
   * Codul fiscal face excepție și se scrie direct: e chiar câmpul în care a căutat, iar
   * valoarea de la ANAF e forma normalizată a ce a tastat (fără `RO`, fără spații).
   */
  const applyAnaf = (found: CuiLookupCompany) => {
    const values: Record<AnafField, string> = {
      name: found.name,
      tradeRegisterNo: found.tradeRegisterNo ?? '',
      legalAddress: found.address.street ?? '',
      city: found.address.city ?? '',
      // Codul vine recunoscut de server; un județ pe care lista nu-l are rămâne de ales de om.
      countyCode: found.address.countyCode ?? '',
      postalCode: found.address.postalCode ?? '',
    };

    const current = formRef.current;
    const filled: Partial<Record<AnafField, string>> = {};
    const different: Partial<Record<AnafField, string>> = {};

    for (const field of ANAF_FIELDS) {
      const value = values[field];
      if (!value) {
        // ANAF n-are câmpul (se întâmplă des). Nu golim ce a scris omul.
        continue;
      }

      if (current[field].trim().length === 0) {
        filled[field] = value;
      } else if (current[field].trim() !== value) {
        different[field] = value;
      }
    }

    setForm({ ...current, ...filled, cif: found.cui });
    setFromAnaf(different);
    setErrors((current) => ({ ...current, cif: undefined }));
  };

  const mutation = useMutation({
    // Starea vine din firma de acum, nu din formular: se schimbă din listă, nu de aici, iar
    // un formular deschis înainte de o dezactivare ar fi reactivat-o la salvare.
    mutationFn: () =>
      company
        ? updateCompany(company.id, { ...form, status: company.status })
        : createCompany(form),
    onSuccess: async (saved) => {
      toast.success(company ? 'Firmă actualizată' : 'Firmă adăugată');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
      ]);
      onSaved?.(saved);
    },
    onError: (error: unknown) => {
      const fieldErrors = toFieldErrors(error);

      if (Object.keys(fieldErrors).length > 0) {
        // Motivul stă sub câmpul lui. **Fără toast** în cazul ăsta: două locuri care spun
        // același lucru, dintre care unul dispare singur, îl pun pe operator să caute.
        setErrors(fieldErrors);
        return;
      }

      // Doar ce nu aparține niciunui câmp: rețea, server căzut, firmă ștearsă între timp.
      toast.error(error instanceof ApiError ? error.message : 'Salvarea a eșuat.');
    },
  });

  const canSave = form.name.trim().length > 0 && form.cif.trim().length > 0;

  /** Propunerea ANAF pentru un câmp deja scris de om, cu butonul de înlocuit. */
  const anafSuggestion = (field: AnafField) => {
    const value = fromAnaf[field];

    return value ? { value, onApply: () => set(field, value) } : undefined;
  };

  return { form, setForm, set, errors, fromAnaf, applyAnaf, anafSuggestion, mutation, canSave };
}

/** Câmpurile formularului, fără butoane: le pune fiecare loc unde îi stau lui. */
export function CompanyFields({ state }: { state: CompanyFormState }) {
  const { form, setForm, set, errors, fromAnaf, applyAnaf, anafSuggestion } = state;
  const disabled = state.mutation.isPending;

  return (
    <>
      <div className="flex items-end gap-x-2">
        <div className="w-[76px] shrink-0">
          <Field label="Icon" id="company-icon">
            <Select
              value={form.icon}
              onValueChange={(value) => setForm((c) => ({ ...c, icon: value as CompanyIconKey }))}
              disabled={disabled}
            >
              <Select.Trigger id="company-icon" aria-label="Iconul firmei">
                <Select.Value />
              </Select.Trigger>
              {/* `z-50`: sertarul e la `z-50`, iar conținutul de Select din Medusa vine
                fără z-index — fără asta, lista s-ar deschide sub sertar. */}
              <Select.Content className="z-50 min-w-0">
                {companyIconKeys.map((key) => {
                  const Icon = companyIcon(key);
                  return (
                    // Doar simbolul: numele („Antenă", „Cablu") nu e o informaţie pe
                    // care operatorul o alege — el alege cum arată. `textValue` rămâne
                    // pus, ca tastarea şi cititorul de ecran să aibă totuşi un cuvânt.
                    <Select.Item key={key} value={key} textValue={companyIconLabel(key)}>
                      <Icon className="size-4 shrink-0" />
                    </Select.Item>
                  );
                })}
              </Select.Content>
            </Select>
          </Field>
        </div>

        <div className="flex-1">
          <Field
            label="Denumire"
            id="company-name"
            required
            error={errors.name}
            suggestion={
              fromAnaf.name
                ? { value: fromAnaf.name, onApply: () => set('name', fromAnaf.name ?? '') }
                : undefined
            }
          >
            <Input
              id="company-name"
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              maxLength={255}
              aria-required
              aria-invalid={errors.name ? true : undefined}
              disabled={disabled}
            />
          </Field>
        </div>
      </div>

      <Field
        label="Denumire scurtă"
        id="company-short-name"
        error={errors.shortName}
        hint="Cum apare firma în interfață."
      >
        <Input
          id="company-short-name"
          value={form.shortName}
          onChange={(event) => set('shortName', event.target.value)}
          maxLength={60}
          placeholder={form.name.trim() || undefined}
          aria-invalid={errors.shortName ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <Field
        label="Cod fiscal"
        id="company-cif"
        required
        error={errors.cif}
        hint="Cu sau fără prefixul `RO`."
      >
        {/*
        Apelul stă aici, în aplicație, iar componenta îl primește ca `onLookup` —
        `libs/fe/ui-kit` e bibliotecă și n-are voie să depindă de clientul de API.
        Vezi tichetul #52. Aceeași componentă va sta pe formularul clienților
        persoane juridice, cu alt `onLookup` și altă mapare.
      */}
        <CuiLookup
          id="company-cif"
          value={form.cif}
          onChange={(next) => set('cif', next)}
          onLookup={lookupCompanyByCui}
          onResult={applyAnaf}
          disabled={disabled}
          required
          invalid={errors.cif !== undefined}
        />
      </Field>

      <Field
        label="Nr. Reg. Com."
        id="company-trade-register"
        error={errors.tradeRegisterNo}
        suggestion={
          fromAnaf.tradeRegisterNo
            ? {
                value: fromAnaf.tradeRegisterNo,
                onApply: () => set('tradeRegisterNo', fromAnaf.tradeRegisterNo ?? ''),
              }
            : undefined
        }
      >
        <Input
          id="company-trade-register"
          value={form.tradeRegisterNo}
          onChange={(event) => set('tradeRegisterNo', event.target.value)}
          maxLength={30}
          aria-invalid={errors.tradeRegisterNo ? true : undefined}
          disabled={disabled}
        />
      </Field>

      {/*
      Comutator, nu bifă: are două stări cu nume, iar cuvântul de lângă el spune pe
      care e acum. Stă lângă datele fiscale, unde e și în bază.
    */}
      <Field label="TVA" id="company-vat-payer">
        <div className="flex h-8 items-center gap-x-3">
          <Switch
            id="company-vat-payer"
            checked={form.vatPayer}
            onCheckedChange={(value) => setForm((c) => ({ ...c, vatPayer: value }))}
            disabled={disabled}
          />
          <Text size="small" className={form.vatPayer ? undefined : 'text-ui-fg-subtle'}>
            {form.vatPayer ? 'Plătitoare' : 'Neplătitoare'}
          </Text>
        </div>
      </Field>

      {/*
      Conturile firmei, trei și fixe (#211): toate ajung pe factură, câte un rând, cu eticheta
      monedei — „Cont RON", „Cont EUR", „Cont RON (Trezorerie)".
    */}
      <Field label="IBAN — cont RON" id="company-iban" error={errors.iban}>
        <Input
          id="company-iban"
          value={form.iban}
          onChange={(event) => set('iban', event.target.value)}
          maxLength={42}
          className="font-mono"
          aria-invalid={errors.iban ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <Field label="IBAN — cont EUR" id="company-iban-eur" error={errors.ibanEur}>
        <Input
          id="company-iban-eur"
          value={form.ibanEur}
          onChange={(event) => set('ibanEur', event.target.value)}
          maxLength={42}
          className="font-mono"
          aria-invalid={errors.ibanEur ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <Field
        label="IBAN — cont de trezorerie"
        id="company-treasury-iban"
        hint="În lei. Contul în care plătesc instituțiile statului."
        error={errors.treasuryIban}
      >
        <Input
          id="company-treasury-iban"
          value={form.treasuryIban}
          onChange={(event) => set('treasuryIban', event.target.value)}
          maxLength={42}
          className="font-mono"
          aria-invalid={errors.treasuryIban ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <Field label="Bancă" id="company-bank" error={errors.bankName}>
        <Input
          id="company-bank"
          value={form.bankName}
          onChange={(event) => set('bankName', event.target.value)}
          maxLength={100}
          aria-invalid={errors.bankName ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <Field
        label="Capital social"
        id="company-share-capital"
        hint="Cum se tipărește pe factură: „200 lei”."
        error={errors.shareCapital}
      >
        <Input
          id="company-share-capital"
          value={form.shareCapital}
          onChange={(event) => set('shareCapital', event.target.value)}
          maxLength={50}
          aria-invalid={errors.shareCapital ? true : undefined}
          disabled={disabled}
        />
      </Field>

      {/*
      Contactul firmei pentru clienți (#224): semnează invitația la semnare. Nu e contactul
      de pe factură, de mai jos.
    */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Telefon" id="company-phone" error={errors.phone}>
          <Input
            id="company-phone"
            value={form.phone}
            onChange={(event) => set('phone', event.target.value)}
            maxLength={30}
            aria-invalid={errors.phone ? true : undefined}
            disabled={disabled}
          />
        </Field>

        <Field label="E-mail" id="company-email" error={errors.email}>
          <Input
            id="company-email"
            type="email"
            value={form.email}
            onChange={(event) => set('email', event.target.value)}
            maxLength={100}
            aria-invalid={errors.email ? true : undefined}
            disabled={disabled}
          />
        </Field>
      </div>

      {/* Contactul de pe factură (BG-6, #211): apare în XML-ul e-Factura, la furnizor. */}
      <Field
        label="Persoană de contact pe factură"
        id="company-invoice-contact-name"
        error={errors.invoiceContactName}
      >
        <Input
          id="company-invoice-contact-name"
          value={form.invoiceContactName}
          onChange={(event) => set('invoiceContactName', event.target.value)}
          maxLength={100}
          aria-invalid={errors.invoiceContactName ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Telefon pe factură"
          id="company-invoice-contact-phone"
          error={errors.invoiceContactPhone}
        >
          <Input
            id="company-invoice-contact-phone"
            value={form.invoiceContactPhone}
            onChange={(event) => set('invoiceContactPhone', event.target.value)}
            maxLength={30}
            aria-invalid={errors.invoiceContactPhone ? true : undefined}
            disabled={disabled}
          />
        </Field>

        <Field
          label="E-mail pe factură"
          id="company-invoice-contact-email"
          error={errors.invoiceContactEmail}
        >
          <Input
            id="company-invoice-contact-email"
            type="email"
            value={form.invoiceContactEmail}
            onChange={(event) => set('invoiceContactEmail', event.target.value)}
            maxLength={100}
            aria-invalid={errors.invoiceContactEmail ? true : undefined}
            disabled={disabled}
          />
        </Field>
      </div>

      {/*
      Adresa sediului, **câmp cu câmp** (#183). XML-ul e-Factura cere localitatea și
      județul separat; un rând de text liber nu se poate despica fără să ghicești.
    */}
      <Field
        label="Stradă și număr"
        id="company-legal-address"
        error={errors.legalAddress}
        suggestion={anafSuggestion('legalAddress')}
      >
        <Input
          id="company-legal-address"
          value={form.legalAddress}
          onChange={(event) => set('legalAddress', event.target.value)}
          maxLength={150}
          aria-invalid={errors.legalAddress ? true : undefined}
          disabled={disabled}
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Localitate"
          id="company-city"
          hint="În București, sectorul: „Sector 2”."
          error={errors.city}
          suggestion={anafSuggestion('city')}
        >
          <Input
            id="company-city"
            value={form.city}
            onChange={(event) => set('city', event.target.value)}
            maxLength={50}
            aria-invalid={errors.city ? true : undefined}
            disabled={disabled}
          />
        </Field>

        <Field
          label="Județ"
          id="company-county"
          error={errors.countyCode}
          suggestion={
            fromAnaf.countyCode
              ? {
                  value: countyName(fromAnaf.countyCode),
                  onApply: () => set('countyCode', fromAnaf.countyCode ?? ''),
                }
              : undefined
          }
        >
          {/* Listă închisă, nu text: un „Cluj" scris de mână pică la ANAF (BR-RO-110). */}
          <Select
            value={form.countyCode || NO_COUNTY}
            onValueChange={(value) => set('countyCode', value === NO_COUNTY ? '' : value)}
            disabled={disabled}
          >
            <Select.Trigger id="company-county" aria-invalid={errors.countyCode ? true : undefined}>
              <Select.Value placeholder="Alege județul" />
            </Select.Trigger>
            <Select.Content className="z-50">
              <Select.Item value={NO_COUNTY}>—</Select.Item>
              {RO_COUNTY_LIST.map((county) => (
                <Select.Item key={county.code} value={county.code}>
                  {county.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </Field>
      </div>

      <Field
        label="Cod poștal"
        id="company-postal-code"
        error={errors.postalCode}
        suggestion={anafSuggestion('postalCode')}
      >
        <Input
          id="company-postal-code"
          value={form.postalCode}
          onChange={(event) => set('postalCode', event.target.value)}
          maxLength={10}
          className="w-40"
          aria-invalid={errors.postalCode ? true : undefined}
          disabled={disabled}
        />
      </Field>

      {/*
      Adresa veche, pe un rând, **doar se arată**. Contractele o mai tipăresc numai cât
      câmpurile de mai sus sunt goale; formularul o trimite înapoi neatinsă, ca salvarea
      — care e o înlocuire a firmei — să n-o golească. Dispare cu o migrare ulterioară.
    */}
      {form.billingAddress ? (
        <Text size="small" className="text-ui-fg-subtle">
          Adresa veche, pe un rând: {form.billingAddress}.{' '}
          {form.legalAddress || form.city || form.countyCode
            ? 'Nu se mai folosește: contractele tipăresc adresa de mai sus.'
            : 'Contractele o tipăresc până completezi adresa de mai sus.'}
        </Text>
      ) : null}
    </>
  );
}
